import { useState, useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ImageUploader } from "@/components/ImageUploader";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useParentCredits } from "@/hooks/useParentCredits";
import { toast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { AlertCircle, ShoppingBag } from "lucide-react";

export default function CreateListing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ptaId, setPtaId] = useState<string | null>(null);
  const [parentId, setParentId] = useState<string | null>(null);
  const { balance, loading: balanceLoading } = useParentCredits(ptaId || undefined);
  const [shopSettings, setShopSettings] = useState<any>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    price: "",
    contact_method: "",
    images: [] as string[]
  });

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    if (!user) return;

    setLoading(true);

    // Get parent data
    const { data: parentData } = await supabase
      .from('parents')
      .select('id, pta_id')
      .eq('user_id', user.id)
      .single();

    if (!parentData) {
      toast({
        title: "Not registered as parent",
        description: "Please register as a parent first",
        variant: "destructive"
      });
      navigate('/parent-portal');
      return;
    }

    setPtaId(parentData.pta_id);
    setParentId(parentData.id);

    // Get shop settings
    const { data: settings } = await supabase
      .from('shop_settings')
      .select('*')
      .eq('pta_id', parentData.pta_id)
      .single();

    if (!settings || !settings.parent_selling_enabled) {
      toast({
        title: "Parent selling disabled",
        description: "Parent selling is not enabled for this PTA",
        variant: "destructive"
      });
      navigate('/marketplace');
      return;
    }

    setShopSettings(settings);

    // Get categories
    const { data: categoriesData } = await supabase
      .from('shop_categories')
      .select('*')
      .eq('pta_id', parentData.pta_id)
      .order('display_order');

    setCategories(categoriesData || []);
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !parentId || !ptaId) {
      toast({
        title: "Error",
        description: "Please log in to create a listing",
        variant: "destructive"
      });
      return;
    }

    if (!termsAccepted) {
      toast({
        title: "Terms Required",
        description: "Please accept the terms of use",
        variant: "destructive"
      });
      return;
    }

    if (formData.images.length === 0) {
      toast({
        title: "Images Required",
        description: "Please upload at least one image",
        variant: "destructive"
      });
      return;
    }

    if ((balance?.credits_available || 0) < shopSettings.credit_cost_per_post) {
      toast({
        title: "Insufficient Credits",
        description: `You need ${shopSettings.credit_cost_per_post} credits to list an item. Please purchase more credits.`,
        variant: "destructive"
      });
      navigate('/marketplace/buy-credits');
      return;
    }

    setSubmitting(true);

    try {
      // Calculate expiry date if configured
      let expiryDate = null;
      if (shopSettings.post_expiry_days) {
        const expiry = new Date();
        expiry.setDate(expiry.getDate() + shopSettings.post_expiry_days);
        expiryDate = expiry.toISOString();
      }

      // Create the listing
      const { error: insertError } = await supabase
        .from('shop_items')
        .insert({
          name: formData.name,
          description: formData.description,
          category: formData.category,
          price: formData.price ? parseFloat(formData.price as string) : null,
          contact_method: formData.contact_method,
          images: formData.images,
          seller_id: parentId,
          pta_id: ptaId,
          status: shopSettings.parent_post_approval_required ? 'pending' : 'active',
          is_approved: !shopSettings.parent_post_approval_required,
          expires_at: expiryDate
        });

      if (insertError) throw insertError;

      // Deduct credits
      const { error: creditError } = await supabase
        .from('parent_credit_transactions')
        .insert({
          parent_id: parentId,
          pta_id: ptaId,
          amount: -shopSettings.credit_cost_per_post,
          transaction_type: 'listing_fee'
        });

      if (creditError) throw creditError;

      toast({
        title: "Success",
        description: shopSettings.parent_post_approval_required 
          ? "Your listing has been submitted for approval" 
          : "Your listing is now live!"
      });

      navigate('/marketplace/my-listings');
    } catch (error: any) {
      console.error('Error creating listing:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create listing",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return null;
  }

  if (loading || balanceLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow container mx-auto px-4 py-8">
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto space-y-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">Create Listing</h1>
            <p className="text-muted-foreground">
              List an item for sale in the marketplace
            </p>
          </div>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Cost:</strong> {shopSettings?.credit_cost_per_post || 1} credit per listing •{" "}
              <strong>Your balance:</strong> {balance?.credits_available || 0} credits
              {shopSettings?.post_expiry_days && (
                <> • <strong>Expires in:</strong> {shopSettings.post_expiry_days} days</>
              )}
            </AlertDescription>
          </Alert>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingBag className="h-5 w-5" />
                Item Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Item Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., School Uniform - Age 7-8"
                    required
                    maxLength={100}
                  />
                  <p className="text-xs text-muted-foreground">{formData.name.length}/100</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.name}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="price">Price (£) - Optional</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="0.00"
                  />
                  <p className="text-xs text-muted-foreground">Leave blank if free or negotiable</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe your item in detail..."
                    required
                    rows={5}
                    maxLength={1000}
                  />
                  <p className="text-xs text-muted-foreground">{formData.description.length}/1000</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact">Contact Method *</Label>
                  <Input
                    id="contact"
                    value={formData.contact_method}
                    onChange={(e) => setFormData({ ...formData, contact_method: e.target.value })}
                    placeholder="e.g., Email me at parent@example.com or Text 07XXX XXXXXX"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    This will be visible to buyers. Include your preferred contact method.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Images * (Max 3, 10MB each)</Label>
                  <ImageUploader
                    images={formData.images}
                    onChange={(images) => setFormData({ ...formData, images })}
                    ptaId={ptaId || ''}
                    itemId="temp"
                  />
                </div>

                <Card className="bg-muted">
                  <CardHeader>
                    <CardTitle className="text-base">Terms of Use</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-muted-foreground whitespace-pre-wrap max-h-60 overflow-y-auto">
                      {shopSettings?.terms_of_use}
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                      <Checkbox
                        id="terms"
                        checked={termsAccepted}
                        onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
                      />
                      <Label htmlFor="terms" className="text-sm font-normal">
                        I have read and agree to the terms of use
                      </Label>
                    </div>
                  </CardContent>
                </Card>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/marketplace')}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting || !termsAccepted}
                    className="flex-1"
                  >
                    {submitting ? "Submitting..." : "Create Listing"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}
